import { DalantStudentItem } from '../../../constants/types/lms/dalant/type';
import TextWinds from '../../common/TextWinds';
import { Button, Checkbox, Input } from 'antd';
import { useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import {
  getDalantStudentList,
  postDalantInvalidate,
  postDalantPoints,
} from '../../../api/utils/lms/dalant/api';
import { useAppDispatch } from '../../../store/hook';
import { getErrorMessages } from '../../../api/utils/util-func';
import { openAlert } from '../../../store/features/modalSlice';
import {
  ALL_CLASS_ID,
  ClassOptionItem,
} from '../../../pages/lms/dalant-mgmt/DalantManagement';
import DalantInvalidateModal from './DalantInvalidateModal';
import DalantByClassCard from './DalantByClassCard';

interface DalantByClassContentsProps {
  selectedClass: ClassOptionItem;
  handleBack: () => void;
}

type DalantActionType = 'EARN' | 'DEDUCT';

export interface DalantFormState {
  actionType: DalantActionType;
  points: number;
  reason: string;
}

const DEFAULT_DALANT_FORM_STATE: DalantFormState = {
  actionType: 'EARN',
  points: 0,
  reason: '',
};

/**
 * 개편 LMS (교사) - 달란트 관리 페이지 > 반별 학생 달란트
 */
const DalantByClassContents = ({
  selectedClass,
  handleBack,
}: DalantByClassContentsProps) => {
  const { classId } = useParams();

  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dalantStudentList, setDalantStudentList] = useState<
    DalantStudentItem[]
  >([]);

  const fetchDalantStudentList = async () => {
    if (!selectedClass) return;

    try {
      setIsLoading(true);

      const response = await getDalantStudentList({
        classId,
        classGroupId:
          selectedClass.id === ALL_CLASS_ID ? null : selectedClass.id,
      });

      const dalantStudentList = (response.students ?? []).sort((a, b) =>
        a.studentName.localeCompare(b.studentName, 'ko'),
      );

      setDalantStudentList(dalantStudentList);
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedClass) return;

    fetchDalantStudentList();
  }, [selectedClass]);

  const [openedStudentId, setOpenedStudentId] = useState<number | null>(null);

  const [dalantFormState, setDalantFormState] = useState<DalantFormState>(
    DEFAULT_DALANT_FORM_STATE,
  );

  const [pendingStudentId, setPendingStudentId] = useState<number | null>(null);

  const [studentNameFilter, setStudentNameFilter] = useState('');

  const filteredDalantStudentList = useMemo(() => {
    const keyword = studentNameFilter.trim().toLowerCase();

    if (!keyword) return dalantStudentList;

    return dalantStudentList.filter((student) =>
      student.studentName.toLowerCase().includes(keyword),
    );
  }, [dalantStudentList, studentNameFilter]);

  const handleDalantSubmit = async (student: DalantStudentItem) => {
    const points = Number(dalantFormState.points);

    if (!Number.isInteger(points) || points <= 0) {
      dispatch(openAlert({ message: '달란트 수량을 1개 이상 입력해주세요.' }));
      return;
    }

    if (!dalantFormState.reason.trim()) {
      dispatch(
        openAlert({ message: '달란트 적립 또는 차감 사유를 입력해주세요.' }),
      );
      return;
    }

    const adjustedPoints =
      dalantFormState.actionType === 'DEDUCT' ? -points : points;

    try {
      setPendingStudentId(student.studentId);

      await postDalantPoints({
        classId,
        studentId: student.studentId,
        points: adjustedPoints,
        reason: dalantFormState.reason.trim(),
      });

      dispatch(
        openAlert({
          message:
            dalantFormState.actionType === 'EARN'
              ? '달란트가 적립되었습니다.'
              : '달란트가 차감되었습니다.',
        }),
      );

      setOpenedStudentId(null);
      setDalantFormState(DEFAULT_DALANT_FORM_STATE);

      await fetchDalantStudentList();
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    } finally {
      setPendingStudentId(null);
    }
  };

  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);

  const [invalidateModalOpen, setInvalidateModalOpen] = useState(false);
  const [invalidateReason, setInvalidateReason] = useState('');
  const [isInvalidatePending, setIsInvalidatePending] = useState(false);

  const filteredStudentIds = filteredDalantStudentList.map(
    (student) => student.studentId,
  );

  const isAllSelected =
    filteredStudentIds.length > 0 &&
    filteredStudentIds.every((id) => selectedStudentIds.includes(id));

  const handleSelectAllStudents = () => {
    setOpenedStudentId(null);
    setDalantFormState(DEFAULT_DALANT_FORM_STATE);

    setSelectedStudentIds((prev) => [
      ...new Set([...prev, ...filteredStudentIds]),
    ]);
  };

  const handleDeselectAllStudents = () => {
    setSelectedStudentIds((prev) =>
      prev.filter((id) => !filteredStudentIds.includes(id)),
    );
  };

  const handleStudentSelect = (studentId: number, checked: boolean) => {
    if (checked) {
      setOpenedStudentId(null);
      setDalantFormState(DEFAULT_DALANT_FORM_STATE);
    }

    setSelectedStudentIds((prev) => {
      if (checked) {
        return [...new Set([...prev, studentId])];
      }

      return prev.filter((id) => id !== studentId);
    });
  };

  const handleCloseInvalidateModal = () => {
    setInvalidateModalOpen(false);
    setInvalidateReason('');
  };

  const handleInvalidateDalant = async () => {
    if (selectedStudentIds.length === 0) {
      dispatch(openAlert({ message: '학생을 선택해주세요.' }));
      return;
    }

    if (!invalidateReason.trim()) {
      dispatch(openAlert({ message: '무효화 사유를 입력해주세요.' }));
      return;
    }

    try {
      setIsInvalidatePending(true);

      await postDalantInvalidate({
        classId,
        studentIds: selectedStudentIds,
        reason: invalidateReason.trim(),
      });

      dispatch(
        openAlert({
          message: '달란트가 무효화되었습니다.',
        }),
      );

      setSelectedStudentIds([]);
      handleCloseInvalidateModal();

      await fetchDalantStudentList();
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    } finally {
      setIsInvalidatePending(false);
    }
  };

  return (
    <>
      <div className="mb-5 flex items-center gap-3">
        <Button
          type="default"
          className="flex items-center gap-2 rounded-lg px-5"
          onClick={() => handleBack()}
        >
          ← 반 목록으로
        </Button>

        <div className="min-w-0">
          <TextWinds type="title_h3" className="truncate">
            {selectedClass.name}
          </TextWinds>
        </div>
      </div>

      <div className="mb-5 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <Input
          type="search"
          allowClear
          className="w-full xl:w-[240px]"
          placeholder="학생명 검색"
          value={studentNameFilter}
          onChange={(event) => setStudentNameFilter(event.target.value)}
        />

        <div className="flex flex-wrap items-center justify-end gap-3">
          <Checkbox
            checked={isAllSelected}
            indeterminate={selectedStudentIds.length > 0 && !isAllSelected}
            disabled={filteredDalantStudentList.length === 0}
            onChange={(event) => {
              if (event.target.checked) {
                handleSelectAllStudents();
              } else {
                handleDeselectAllStudents();
              }
            }}
          >
            전체 선택
          </Checkbox>

          <div className="h-5 w-px bg-gray3" />

          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>
              선택 <strong>{selectedStudentIds.length}</strong>명
            </span>

            <Button
              danger
              disabled={selectedStudentIds.length === 0}
              onClick={() => setInvalidateModalOpen(true)}
            >
              달란트 무효화
            </Button>
          </div>
        </div>
      </div>

      {!isLoading && filteredDalantStudentList.length === 0 ? (
        <div className="flex h-[280px] items-center justify-center rounded-xl border border-dashed border-gray4 bg-gray0 px-4 text-center">
          <TextWinds type="content_body2" color="gray6">
            조회된 학생이 없습니다.
          </TextWinds>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
          {filteredDalantStudentList.map((student) => {
            const isOpened = openedStudentId === student.studentId;
            const isSelected = selectedStudentIds.includes(student.studentId);
            const isPending = pendingStudentId === student.studentId;

            return (
              <DalantByClassCard
                key={student.studentId}
                student={student}
                isOpened={isOpened}
                isSelected={isSelected}
                isPending={isPending}
                formState={dalantFormState}
                onSelect={(checked) =>
                  handleStudentSelect(student.studentId, checked)
                }
                onToggleOpen={() => {
                  if (isOpened) {
                    setOpenedStudentId(null);
                    setDalantFormState(DEFAULT_DALANT_FORM_STATE);
                    return;
                  }

                  setSelectedStudentIds([]);
                  setOpenedStudentId(student.studentId);
                  setDalantFormState(DEFAULT_DALANT_FORM_STATE);
                }}
                onChangeFormState={(value) =>
                  setDalantFormState((prev) => ({
                    ...prev,
                    ...value,
                  }))
                }
                onSubmit={() => handleDalantSubmit(student)}
              />
            );
          })}
        </div>
      )}

      <DalantInvalidateModal
        open={invalidateModalOpen}
        selectedCount={selectedStudentIds.length}
        reason={invalidateReason}
        isPending={isInvalidatePending}
        onChangeReason={setInvalidateReason}
        onClose={handleCloseInvalidateModal}
        onSubmit={handleInvalidateDalant}
      />
    </>
  );
};

export default DalantByClassContents;
